import { Autocomplete, TextField } from '@mui/material';
import {
  Controller,
  type Control,
  type FieldPath,
  type FieldPathValue,
  type FieldValues,
} from 'react-hook-form';
import type { SyntheticEvent } from 'react';
import type { Identifiable } from './SelectField';
import React from 'react';
import { useIntl } from 'react-intl';

type AutoCompleteProps<TFieldValues extends FieldValues, TOption extends object> = {
  id?: string;
  fieldPath: FieldPath<TFieldValues>;
  control: Control<TFieldValues>;
  options: TOption[];
  label: string;
  getOptionLabel: (option: TOption) => string;
  getOptionValue: (option: TOption) => string | number;
  onChange?: (event: SyntheticEvent, option: TOption | null) => void;
  defaultValue?: FieldPathValue<TFieldValues, FieldPath<TFieldValues>>;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  inputRef?: React.Ref<HTMLInputElement>;
  optionsPlacement?:
    | 'bottom'
    | 'top'
    | 'auto'
    | 'bottom-start'
    | 'bottom-end'
    | 'top-start'
    | 'top-end';
};

function AutoComplete<TFieldValues extends FieldValues, TOption extends object>({
  id,
  fieldPath,
  control,
  options,
  label,
  getOptionLabel,
  getOptionValue,
  onChange,
  defaultValue,
  placeholder,
  disabled,
  required,
  inputRef,
  optionsPlacement,
}: AutoCompleteProps<TFieldValues, TOption>) {
  const intl = useIntl();
  const labelFn = getOptionLabel ?? ((opt: Identifiable) => opt?.name ?? '');

  const valueFn = getOptionValue ?? ((opt: Identifiable) => opt?.id ?? '');

  return (
    <Controller
      name={fieldPath}
      control={control}
      defaultValue={defaultValue}
      rules={required ? { required: intl.formatMessage({ id: 'validation.required' }) } : undefined}
      render={({ field, fieldState }) => {
        const selected = options.find(opt => valueFn(opt) === field.value) ?? null;

        return (
          <Autocomplete
            id={id}
            noOptionsText={intl.formatMessage({ id: 'autocomplete.noOptions' })}
            options={options}
            disabled={disabled}
            renderOption={(props, option) => (
              <li {...props} key={valueFn(option)}>
                {labelFn(option)}
              </li>
            )}
            value={selected}
            getOptionLabel={labelFn}
            isOptionEqualToValue={(opt, val) => valueFn(opt) === valueFn(val)}
            onChange={(e, option) => {
              onChange?.(e, option);
              field.onChange(option ? valueFn(option) : null);
            }}
            slotProps={{
              popper: {
                placement: optionsPlacement,
              },
            }}
            renderInput={params => (
              <TextField
                {...params}
                label={label}
                placeholder={placeholder}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                inputRef={(element: HTMLInputElement) => {
                  field.ref(element);

                  if (inputRef) {
                    if (typeof inputRef === 'function') {
                      inputRef(element);
                    } else {
                      inputRef.current = element;
                    }
                  }
                }}
              />
            )}
          />
        );
      }}
    />
  );
}

export default AutoComplete;
